/* eslint-disable */
import Vue from 'vue'

// JWT parsing utility function. Moved here to be self-contained within the module.
function parseJwt(token) {
    if (!token) return null;
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) {
        console.error("Error parsing JWT", e);
        return null;
    }
}

const defaultState = () => {
    return {
        accessToken: null,
        refreshToken: null,
        authenticated: false,
        tokenExpiry: null,
        jwtPayload: null,
        token: null,
        loginLoop: 0,
        isTokenExpires: false
   }
};

const state = defaultState;


const actions = {
    // Action to be called by Verify.vue
    async verifyAuthentication({ commit, dispatch }, token) {
        try {
            // We need a clean axios instance to avoid an interceptor loop
            const axiosInstance = Vue.prototype.$axios.create();
            const response = await axiosInstance.get(Vue.prototype.$server + 'verify/auth', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data.success) {
                // On successful verification, commit the data to the store
                const payload = parseJwt(token);
                commit('setAccessToken', token);
                commit('setToken', token); // For the logout URL
                commit('setJwtPayload', payload);
                commit('authenticated', true);

                if (response.data.type === 'client' && response.data.org?.length > 0) {
                    const orgData = response.data.org[0];
                    // Dispatch action to select the organisation globally
                    dispatch('organisation/selectOrganisation', {
                        id: orgData.organisation_id,
                        name: orgData.organisation_name || orgData.organisation
                    }, { root: true });
                }

                return response.data; // Return { success, type, org } to the component
            } else {
                throw new Error('API verification failed');
            }
        } catch (error) {
            // On any failure, trigger the full logout process
            dispatch('logout', 'Your session is invalid or has expired. Please log in again.');
            throw error; // Propagate error to be caught by the component
        }
    },

    async checkAuthentication({ commit, dispatch, state }) {
        // Check if the user is already authenticated
        if (state.authenticated) {
            return;
        }

        // Check for the token in local storage
        const token = localStorage.getItem('accessToken');
        if (token) {
            try {
                // If the token exists, verify it
                await dispatch('verifyAuthentication', token);
            } catch (error) {
                // If the token is invalid, log out the user
                dispatch('logout', 'Your session is invalid or has expired. Please log in again.');
            }
        }
    },

    resetState ({commit}) {
        commit('resetState');
    },

    // This action will be needed by the axios interceptor.
    async refreshToken({ commit, state, dispatch }) {
        const token = state.accessToken;
        const user = state.jwtPayload;

        if (!token || !user?.jwt_refresh_token) {
            throw new Error("No refresh token available.");
        }

        try {
            const axiosInstance = Vue.prototype.$axios.create();
            const refreshResponse = await axiosInstance.post(`${Vue.prototype.$auth}users/token/refresh`, {
                access_token: token,
                refresh_token: user.jwt_refresh_token
            });

            const newAccessToken = refreshResponse.data.user.access_token;
            commit('setAccessToken', newAccessToken);
            return newAccessToken;
        } catch (error) {
            throw error; // Propagate error to the interceptor
        }
    },

    async logout({ commit, state, dispatch }, errorMessage = "") {
        const returnUrl = encodeURIComponent(Vue.prototype.$ui + 'verify');
        let logoutUrl = `${Vue.prototype.$auth_ui}login/?logout=true&redirect_url=${returnUrl}`;

        if (state.token) {
            logoutUrl += `&token=${state.token}`;
        }

        if (state.loginLoop > 0) {
            logoutUrl += `&login_loop=${state.loginLoop}`;
        }

        if (state.isTokenExpires) {
            logoutUrl += `&error=TOKEN_EXPIRE`;
        }

        if (errorMessage) {
            logoutUrl += `&message=${encodeURIComponent(errorMessage)}`;
        }

        // Clear Vuex state
        commit("resetState");
        dispatch('organisation/clearOrganisation', null, { root: true });

        // Explicitly remove persisted state from localStorage for security.
        // This is a safeguard in addition to vuex-persistedstate's handling.
        localStorage.removeItem('pentester');

        window.location.href = logoutUrl;
    }
}


const mutations = {
    authenticated(state, authenticated) {
        state.authenticated = authenticated;
    },

    setAccessToken(state, accessToken){
        state.accessToken = accessToken;
    },

    setToken(state, token){
        state.token = token;
    },

    setTokenExpires(state){
        state.isTokenExpires = true;
    },

    setJwtPayload(state, payload) {
        state.jwtPayload = payload;
        state.tokenExpiry = payload.expires_at;
    },

    resetState(state) {
        Object.assign(state, defaultState());
    },

    setLoginLoop(state, loginLoop) {
        state.loginLoop = loginLoop > 0 ? loginLoop : 0;
    },
};

const getters = {
    isAuthenticated: state => !!state.accessToken,
    accessToken: state => state.accessToken,
    user: state => state.jwtPayload,

    // Role checks based on the JWT payload
    isSuperAdmin: state => state.jwtPayload?.roles?.includes("super_admin") || false,
    isPentester: state => state.jwtPayload?.roles?.includes("penetration_tester") || false,

    isOrgAdmin: state => {
        const user = state.jwtPayload;
        if (user && user.roles == null && user.organisation_role?.length > 0) {
            const orgRole = user.organisation_role[0];
            const product = orgRole.products?.find(p => p.product === "pentester_platform");
            return orgRole.is_organisation_admin && (product?.roles?.length > 0 || false);
        }
        return false;
    },

    isOrgPentester: state => {
        const user = state.jwtPayload;
        if (user && user.roles == null && user.organisation_role?.length > 0) {
            const product = user.organisation_role[0].products?.find(p => p.product === "pentester_platform");
            return product?.roles?.length > 0 || false;
        }
        return false;
    }
};

export default {
  name: 'auth',
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};